import * as _ from 'lodash';
import { useEffect, useState } from 'react';
import usePrevious from '@nkr/services/use-previous';
import { ICenterBase, IPosition } from '@nkr/models/index';
import { SwapManagementAction } from './swap-managerment-action';
import { ISwapManagementPosition } from './swap-management-position';
import { Button, Modal } from 'react-bootstrap';
import { IAvailableRecipient } from '@nkr/store/index';
import Content from './content';

interface IProps {
    show: boolean,
    chainId: number,
    positions: IPosition[],
    recipientList: IAvailableRecipient[],
    centerList: ICenterBase[],
    isStandalone: boolean,
    onAction: (
        action: SwapManagementAction, clusterForAction: number, positionForAction: number,
        donorForAction: number, recipientForAction: number, replacementRecipientId: number, bridgeDonorId: number) => void,
    onClose: () => void
}

const SwapManagement = (props: IProps) => {
    const [action, setAction] = useState(0);
    const [currentIndex, setCurrentIndex] = useState(-1);

    const [center, setCenter] = useState(0);
    const [recipient, setRecipient] = useState(0);
    const [donor, setDonor] = useState(0);

    const previousShow = usePrevious(props.show)
    const previousPositions = usePrevious(props.positions);

    useEffect(() => {
        if (props.show && previousShow == false) {
            reset();
        }

        if (_.isEqual(previousPositions, props.positions) == false) {
            reset();
        }
    }, [props.show, props.positions]);

    const reset = () => {
        setAction(0);
        setCenter(0);
        setRecipient(0);
        setDonor(0);
    };

    const getSortedPositions = (): ISwapManagementPosition[] => {
        let currentCluster = -1;

        let sortedPositions = _.sortBy(props.positions, [x => x.clusterNumber, x => x.positionNumber]).map((position, index) => {
            let results = { index, ...position, isNotCurrent: currentCluster != position.clusterId, highlightRow: false, highlightRecipient: false, highlightDonor: false };
            currentCluster = position.clusterId;
            return results;
        });

        let currentPosition = sortedPositions.find(x => x.index == currentIndex);

        let donorForAction = currentPosition != null ? currentPosition.donor.id : 0;

        let clusterForAction = currentPosition != null ? currentPosition.clusterId : 0;

        sortedPositions = sortedPositions.map((position, index) => {
            let highlightRow = false;
            let highlightRecipient = false;
            let highlightDonor = false;

            if ([SwapManagementAction.Bridge, SwapManagementAction.NewChainAsVoucher].indexOf(action) >= 0) {
                if (index == currentIndex) {
                    highlightRow = true;
                }
            }

            if (action == SwapManagementAction.AddRecipient) {
                if (index == currentIndex) {
                    highlightRecipient = true;
                }
            }

            if (action == SwapManagementAction.RemovePair || action == SwapManagementAction.ReplacePair) {
                let d = sortedPositions.find(x => x.index == currentIndex);

                if (d != null) {
                    let previousPosition = sortedPositions.find(x => x.index == currentIndex - 1);

                    if (previousPosition != null) {
                        if (previousPosition == position) {
                            highlightRecipient = true;
                        } if (index == currentIndex) {
                            highlightRow = true;
                        }
                    }
                }
            }

            if (action == SwapManagementAction.ResetCluster) {
                if (position.clusterId == clusterForAction) {
                    highlightRow = true;
                }
            }

            position.highlightRow = highlightRow;
            position.highlightRecipient = highlightRecipient;
            position.highlightDonor = highlightDonor;

            return position;
        });

        return sortedPositions;
    };

    const handleCenterChange = (value: number) => {
        let recipient = props.recipientList.find(x => x.centerId == value);
        let recipientId = recipient != null ? recipient.id : 0;

        let bridgeDonor = recipient != null ? _.first(recipient.pairedDonors) : null;
        let bridgeDonorId = bridgeDonor != null ? bridgeDonor.id : 0;

        setCenter(value);
        setRecipient(recipientId);
        setDonor(bridgeDonorId);
    };

    const handleRecipientChange = (value: number) => {
        let recipient = props.recipientList.find(x => x.id == value);
        let bridgeDonor = recipient != null ? _.first(recipient.pairedDonors) : null;
        let bridgeDonorId = bridgeDonor != null ? bridgeDonor.id : 0;

        setRecipient(value);
        setDonor(bridgeDonorId);
    };

    const handleDonorChange = (value: number) => {
        setDonor(value);
    }

    const handleAction = (action: SwapManagementAction, index: number) => {
        let sortedPositions = getSortedPositions();

        let position = sortedPositions.find(x => x.index == index);

        setAction(action);
        setCurrentIndex(index);

        if (action == SwapManagementAction.ReplacePair || action == SwapManagementAction.ReplaceRecipient || action == SwapManagementAction.AddRecipient) {
            let center = _.first(position.recipient.id != -1 ? props.centerList : props.centerList.filter(x => x.hasPairs));
            let centerId = center != null ? center.id : 0;

            let recipient = props.recipientList.find(x => x.centerId == centerId);
            let recipientId = recipient != null ? recipient.id : 0;

            let bridgeDonor = recipient != null ? _.first(recipient.pairedDonors) : null;
            let bridgeDonorId = bridgeDonor != null ? bridgeDonor.id : 0;

            setCenter(centerId);
            setRecipient(recipientId);
            setDonor(bridgeDonorId);
        }

        if (action == SwapManagementAction.RemovePair) {
            position = sortedPositions.find(x => x.index == index - 1);
        }
    };

    const handleResetCluster = (clusterId: number, firstDonorId: number) => {
        setAction(SwapManagementAction.ResetCluster);

        let position = getSortedPositions().find(x => x.clusterId == clusterId && x.donor.id == firstDonorId);

        if (position != null) {
            setCurrentIndex(position.index);
        }
    }

    const handleYes = () => {
        let position = getSortedPositions().find(x => x.index == currentIndex);
        let donorForAction = position.donor.id;
        let recipientForAction = position.recipient.id;
        let positionForAction = position.positionNumber;
        let clusterForAction = position.clusterId;

        let pairRecipient = 0;
        let pairRecipientPosition = 0;

        if (action == SwapManagementAction.RemovePair) {
            let position = getSortedPositions().find(x => x.index == currentIndex - 1);

            pairRecipient = position.recipient.id;
            pairRecipientPosition = position.positionNumber;
        }

        if (action == SwapManagementAction.RemovePair) {
            props.onAction(action, clusterForAction, pairRecipientPosition, donorForAction, pairRecipient, recipient, donor);
        } else {
            props.onAction(action, clusterForAction, positionForAction, donorForAction, recipientForAction, recipient, donor);
        }

        setAction(0);
        setCurrentIndex(0);
    };

    const handleNo = () => {
        setAction(0);
        setCurrentIndex(0);
    };

    const maxClusterNumber = _.max(props.positions.map(x => x.clusterNumber));
    const sortedPositions = getSortedPositions();

    const position = sortedPositions.find(x => x.index == currentIndex);
    const donorToBridgeAlias = position != null ? position.donor.alias : '';

    const clusterForAction = position != null ? position.clusterId : 0;

    const firstPositionOfCluster = sortedPositions.findIndex(x => x.clusterId == clusterForAction);
    const clusterNumber = firstPositionOfCluster >= 0 ? sortedPositions[firstPositionOfCluster].clusterNumber : 0;

    const lastPositionAcrossAllClusters = sortedPositions[sortedPositions.length - 1];

    const lastRecipientAlias = lastPositionAcrossAllClusters?.recipient != null ? lastPositionAcrossAllClusters.recipient.alias : '';
    const lastRecipientCenter = lastPositionAcrossAllClusters?.recipient != null ? lastPositionAcrossAllClusters.recipient.center.alias : '';
    const lastPairCenter = lastPositionAcrossAllClusters?.donor.center.alias;

    if (props.isStandalone) {
        return (
            <Content
                sortedPositions={sortedPositions}
                lastPositionAcrossAllClusters={lastPositionAcrossAllClusters}
                action={action}
                clusterNumber={clusterNumber}
                index={currentIndex}
                firstPositionOfCluster={firstPositionOfCluster}
                maxClusterNumber={maxClusterNumber}
                center={center}
                recipient={recipient}
                donor={donor}
                donorToBridgeAlias={donorToBridgeAlias}
                lastRecipientCenter={lastRecipientCenter}
                lastRecipientAlias={lastRecipientAlias}
                lastPairCenter={lastPairCenter}
                recipientList={props.recipientList}
                centerList={props.centerList}
                onAction={handleAction}
                onResetCluster={handleResetCluster}
                onCenterChange={handleCenterChange}
                onRecipientChange={handleRecipientChange}
                onDonorChange={handleDonorChange}
                onYes={handleYes}
                onNo={handleNo} />
        )
    }

    return (
        <Modal backdrop='static' bsSize='lg' show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>Swap Toolbox, Chain {props.chainId}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Content
                    sortedPositions={sortedPositions}
                    lastPositionAcrossAllClusters={lastPositionAcrossAllClusters}
                    action={action}
                    clusterNumber={clusterNumber}
                    index={currentIndex}
                    firstPositionOfCluster={firstPositionOfCluster}
                    maxClusterNumber={maxClusterNumber}
                    center={center}
                    recipient={recipient}
                    donor={donor}
                    donorToBridgeAlias={donorToBridgeAlias}
                    lastRecipientCenter={lastRecipientCenter}
                    lastRecipientAlias={lastRecipientAlias}
                    lastPairCenter={lastPairCenter}
                    recipientList={props.recipientList}
                    centerList={props.centerList}
                    onAction={handleAction}
                    onResetCluster={handleResetCluster}
                    onCenterChange={handleCenterChange}
                    onRecipientChange={handleRecipientChange}
                    onDonorChange={handleDonorChange}
                    onYes={handleYes}
                    onNo={handleNo} />
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={props.onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
}

export default SwapManagement;